import {
    Component,
    Input,
    OnChanges,
    SimpleChanges,
    ViewChild,
    ElementRef,
    AfterViewInit,
    SecurityContext,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { MarkdownService } from 'ngx-markdown';

import { createTILinks } from '../../core/pipes/tracker-item/ti-links.functions';
import { createGitLinks } from '../../core/pipes/git-links/git-links.functions';
import { looksLikeHtml } from '../../core/services/markdown-content.functions';
import { MarkdownEditorService } from '../../core/services/core.markdown-editor.service';

/**
 * Read-only Markdown viewer backed by ngx-markdown's `MarkdownService`
 * (which wraps the `marked` library).
 *
 * Note on Markdown vs HTML detection: the underlying `marked` parser treats
 * raw HTML as a valid block in Markdown (Markdown is a superset of HTML), so
 * legacy HTML-only content passes through largely untouched while new
 * Markdown content is converted to HTML. There is no explicit "is this
 * Markdown or HTML?" switch — both inputs render correctly without one.
 *
 * Tracker item refs (`#1234`, `[#1234]`) and `git:<hash>` commit refs in the
 * rendered output are post-processed via the shared `createTILinks`/
 * `createGitLinks` helpers so they become real anchors — the same behaviour
 * the legacy `renderTiLinks` pipe applied to server-rendered HTML.
 */
@Component({
    selector: 'gf-markdown-viewer',
    template: require('./gf-markdown-viewer.component.html'),
})
export class GfMarkdownViewerComponent implements AfterViewInit, OnChanges {
    /** Marks rendered Markdown as whitespace-significant (see class docblock). */
    static readonly PRESERVE_WHITESPACE_CLASS = 'gf-markdown-viewer--preserve-whitespace';

    @ViewChild('viewerContainer', { static: false }) viewerContainer!: ElementRef; // set by Angular after the view initializes

    /** Markdown source, compiled to HTML and sanitised on the client. */
    @Input() source: string = '';

    /**
     * Optional activity-type hint forwarded to `createTILinks` so deleted-TI
     * references render with strike-through styling instead of a live link.
     */
    @Input() activityType: string = '';

    /**
     * Project unix name (or id) used to resolve `git:<hash>` refs to a commit
     * link. Left empty, those refs are not linked (no repo to resolve against).
     */
    @Input() project: string = '';

    constructor(
        private markdownService: MarkdownService,
        private sanitizer: DomSanitizer,
        private markdownEditorService: MarkdownEditorService
    ) {}

    ngAfterViewInit(): void {
        this.render();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.source && !changes.source.firstChange) {
            this.render();
        }
    }

    private render(): void {
        const host: HTMLElement | null = this.viewerContainer?.nativeElement;
        if (!host) {
            return;
        }
        const source = this.source || '';
        // `breaks: true` matches ToastUI (and GitHub-style comments): a single
        // newline is a line break, not a soft wrap that renders as a space.
        const compiled = this.markdownService.parse(source, {
            markedOptions: { gfm: true, breaks: true },
        });
        const rawHtml = typeof compiled === 'string' ? compiled : '';
        host.classList.toggle(GfMarkdownViewerComponent.PRESERVE_WHITESPACE_CLASS, !looksLikeHtml(source));
        // Tracker/field markdown is stored raw (not escaped at save) and rendered
        // here on the client, so sanitise before hosting via innerHTML — this is
        // the trust boundary for these frontend-rendered surfaces.
        host.innerHTML = this.sanitizer.sanitize(SecurityContext.HTML, rawHtml) || '';
        // Syntax-highlight code blocks (with copy buttons), render Mermaid diagrams,
        // and render KaTeX math, before TILinks walks the DOM below.
        this.markdownEditorService.enhanceStaticHtml(host);
        // Give tables the app's Bootstrap styling (raw markdown output has none).
        host.querySelectorAll('table:not(.table)').forEach((t: Element) =>
            t.classList.add('table', 'table-hover'));
        this.applyTILinks();
    }

    /**
     * Walk the rendered DOM's text nodes and rewrite tracker-item and
     * `git:<hash>` refs into anchors. We avoid `innerHTML = createTILinks(innerHTML)`
     * because that would re-parse already-sanitised HTML.
     */
    private applyTILinks(): void {
        const host: HTMLElement | null = this.viewerContainer?.nativeElement;
        if (!host) {
            return;
        }
        const walker = document.createTreeWalker(host, NodeFilter.SHOW_TEXT, {
            acceptNode: (n: Text) => {
                // Skip text inside <a>, <code>, <pre> — TI/git refs there are
                // either already linked or intentionally literal.
                let parent: Node | null = n.parentNode;
                while (parent && parent !== host) {
                    const tag = (parent as HTMLElement).tagName;
                    if (tag === 'A' || tag === 'CODE' || tag === 'PRE') {
                        return NodeFilter.FILTER_REJECT;
                    }
                    parent = parent.parentNode;
                }
                return /\[?#\d+|\bgit:[0-9a-fA-F]{7,40}\b/.test(n.nodeValue || '')
                    ? NodeFilter.FILTER_ACCEPT
                    : NodeFilter.FILTER_REJECT;
            },
        });
        const targets: Text[] = [];
        let cur: Node | null;
        while ((cur = walker.nextNode())) {
            targets.push(cur as Text);
        }
        for (const text of targets) {
            const withTiLinks = createTILinks(text.nodeValue || '', this.activityType);
            const linked = createGitLinks(withTiLinks, this.project);
            if (linked === text.nodeValue) {
                continue;
            }
            const wrapper = document.createElement('span');
            wrapper.innerHTML = linked;
            text.parentNode?.replaceChild(wrapper, text);
            while (wrapper.firstChild) {
                wrapper.parentNode?.insertBefore(wrapper.firstChild, wrapper);
            }
            wrapper.parentNode?.removeChild(wrapper);
        }
    }
}
